import { ChangeEvent, Dispatch, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoLevelErvk } from 'APIPackage/CatalogsModule/interfaces/IKnoLevelErvk';

import { Modal } from '../../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import styles from './DictionaryKnoOrganisationsModal.module.scss';
import { IRootState } from '../interfaces/IRootState';
import { RootActions } from '../types/RootActions';
import {
  userInfoGetKnoAdminLevels,
  userInfoKnoOrganizationsReset,
  userInfoSetOrganisationsAdminLevelCode,
  userInfoSetOrganisationsParentId,
  userInfoSetOrganisationsSearchQuery,
} from '../../../PersonalAccountPackage/UserInfoModule/actions/userInfoActions';
import { IKnoOrganisationChild } from '../../../APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import {
  getUserInfoHighlightedSelectedOrganisations,
  getUserInfoKnoOrganisations,
  getUserInfoOrganisationsAdminLevelCode,
  getUserInfoOrganisationsParentId,
  getUserInfoOrganisationsSearchQuery,
  getUserInfoOrganisationsTotalCount,
} from '../../../PersonalAccountPackage/UserInfoModule/selectors/userInfoSelectors';
import {
  dictionariesKnoOrganisationsGet,
  dictionariesKnoOrganisationsGetNextPage,
} from '../../DictionariesModule/actions/dictionariesActions';
import { SelectOrganisations } from '../../../UIPackage/SelectOrganisationsModule/components/SelectOrganisations';
import { Notification } from '../../NotificationModule/components/Notification';
import { INotification } from '../../NotificationModule/interfaces/INotification';
import { NoticeTypesEnum } from '../../NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../NotificationModule/enums/MessageTypesEnum';
import { getCatalogKnoAdminLevelsSelectOptions } from '../../CatalogsModule/selectors/catalogsSelectors';

const SEARCH_INPUT_MIN_ERROR = 'Поле поиска должно содержать не менее 4 символов';
const SEARCH_INPUT_MAX_ERROR = 'Максимальное количество символов: 500';
const SAVE_ERROR_NOTIFICATION: INotification = {
  noticeType: NoticeTypesEnum.ERROR,
  messageType: MessageTypesEnum.SAVE_KNM,
  renderContent: 'Контрольному (надзорному) органу уже предоставлен доступ к мероприятию ',
};

interface IOrganisation extends IKnoOrganisationChild {
  name: string;
}

interface IDictionaryKnoOrganisationsModalProps {
  visible?: boolean;
  onClose: () => void;
  onApply: (organisations: IKnoOrganisationChild[]) => void;
}

interface IDictionaryKnoOrganisationsModalStoreProps {
  knoOrganizationAdminLevelsSelectOptions: IDictionarySelectOption<IKnoLevelErvk>[];
  adminLevelCode?: string;
  searchQuery: string | undefined;
  organisationId: string | undefined;
  knoOrganisations: IKnoOrganisationChild[];
  totalCount: number;
  highlightedOrganisations: IKnoOrganisationChild[] | undefined;
}

const DictionaryKnoOrganisationsModalWithoutStore = ({
  visible,
  onClose,
  onApply,
  knoOrganizationAdminLevelsSelectOptions,
  adminLevelCode,
  searchQuery,
  organisationId,
  knoOrganisations,
  totalCount,
  highlightedOrganisations,
}: IDictionaryKnoOrganisationsModalProps & IDictionaryKnoOrganisationsModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchInputError, setSearchInputError] = useState('');
  const [currentSelectedOrganisationsList, setCurrentSelectedOrganisationsList] = useState<Array<IOrganisation>>([]);
  const [isShowNotification, setIsShowNotification] = useState(false);

  const handleAfterClose = useCallback(() => {
    dispatch(userInfoKnoOrganizationsReset());
    setCurrentSelectedOrganisationsList([]);
  }, [dispatch]);

  useEffect(() => {
    if (visible) {
      dispatch(userInfoGetKnoAdminLevels());
    } else {
      handleAfterClose();
    }
    setIsShowNotification(false);
  }, [dispatch, visible, setIsShowNotification]);

  useEffect(() => {
    if (visible && knoOrganizationAdminLevelsSelectOptions[0]) {
      dispatch(userInfoSetOrganisationsAdminLevelCode(knoOrganizationAdminLevelsSelectOptions[0].code));
      dispatch(dictionariesKnoOrganisationsGet());
    }
  }, [knoOrganizationAdminLevelsSelectOptions, visible, dispatch]);

  const handleSearchInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setSearchInputValue(newValue);

    if (newValue.length > 500) {
      setSearchInputError(SEARCH_INPUT_MAX_ERROR);
    } else if (newValue.length === 0 || newValue.length > 3) {
      setSearchInputError('');
      dispatch(userInfoSetOrganisationsSearchQuery(newValue));
    } else {
      setSearchInputError(SEARCH_INPUT_MIN_ERROR);
    }
  }, []);

  const handleSearch = useCallback(() => {
    if (searchInputValue.length > 500) {
      setSearchInputError(SEARCH_INPUT_MAX_ERROR);
    } else if (searchInputValue.length === 0 || searchInputValue.length > 3) {
      setSearchInputError('');
      dispatch(userInfoSetOrganisationsSearchQuery(searchInputValue));
      dispatch(dictionariesKnoOrganisationsGet());
    } else {
      setSearchInputError(SEARCH_INPUT_MIN_ERROR);
    }
  }, [searchInputValue, dispatch]);

  const handleOrganisationsPageLoad = useCallback(() => {
    if (knoOrganisations.length < totalCount) {
      dispatch(dictionariesKnoOrganisationsGetNextPage());
    }
  }, [dispatch, knoOrganisations.length, totalCount]);

  const handleTextInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const currentAdminLevelSelectOption = useMemo(
    () =>
      adminLevelCode
        ? knoOrganizationAdminLevelsSelectOptions.find(item => item.code === adminLevelCode)
        : knoOrganizationAdminLevelsSelectOptions[0],
    [adminLevelCode, knoOrganizationAdminLevelsSelectOptions],
  );

  const handleAdminLevelsSelectChange = useCallback(
    (value: IDictionarySelectOption<IKnoLevelErvk>) => {
      if (adminLevelCode !== value.code) {
        dispatch(userInfoSetOrganisationsAdminLevelCode(value.code));
        dispatch(userInfoSetOrganisationsParentId());
        dispatch(dictionariesKnoOrganisationsGet());
      }
    },
    [adminLevelCode, dispatch],
  );

  const handleSelectedOrganisationsChange = useCallback(
    (organisations: IOrganisation[]) => setCurrentSelectedOrganisationsList(organisations),
    [],
  );

  const handleOrganisationsParentIdChange = useCallback(
    (id?: string) => {
      dispatch(userInfoSetOrganisationsParentId(id));
      dispatch(dictionariesKnoOrganisationsGet());
    },
    [dispatch],
  );

  const organisationsList = useMemo(
    () =>
      knoOrganisations.map(item => ({
        ...item,
        name: item.fullName,
        guid: item.id,
      })),
    [currentAdminLevelSelectOption?.label, knoOrganisations, searchQuery],
  );

  const handleApply = useCallback(() => {
    onApply(currentSelectedOrganisationsList);
  }, [currentSelectedOrganisationsList, onApply]);

  const handleCloseNotification = useCallback(() => {
    setIsShowNotification(false);
  }, [setIsShowNotification]);

  useEffect(() => {
    if (highlightedOrganisations?.length) {
      setIsShowNotification(true);
    }
  }, [highlightedOrganisations, setIsShowNotification]);

  return (
    <Modal visible={visible} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title="Поиск контрольного (надзорного) органа" />
      {isShowNotification && (
        <div className={styles.Notification}>
          <Notification notice={SAVE_ERROR_NOTIFICATION} close={handleCloseNotification} />
        </div>
      )}
      <ModalBody size="content" border="bottom">
        <Row notWrap>
          <FieldBlock margin="right">
            <TextInput
              placeholder="Введите ОГРН или часть полного (краткого) наименования КНО"
              error={searchInputError}
              onChange={handleSearchInputChange}
              onKeyDown={handleTextInputKeyDown}
            />
          </FieldBlock>
          <Button text="Искать" color="primary" size="medium" type="button" onClick={handleSearch} />
        </Row>
        <div className={styles.SelectLabel}>Уровень органа государственной власти</div>
        <SelectInput
          className={styles.SelectInput}
          options={knoOrganizationAdminLevelsSelectOptions}
          value={currentAdminLevelSelectOption}
          onChange={handleAdminLevelsSelectChange}
          isClearable={false}
        />
        <SelectOrganisations
          isNavigationVisible={!searchQuery?.length}
          isNavigationRoot={!organisationId}
          rootNavigation={currentAdminLevelSelectOption?.label || ''}
          organisationsTitle="Доступные организации"
          selectedOrganisationsTitle="Выбранные организации"
          organisations={organisationsList}
          selectedOrganisations={currentSelectedOrganisationsList}
          highlightedSelectedOrganisations={highlightedOrganisations}
          isLoadButtonVisible={knoOrganisations.length < totalCount}
          onSelected={handleSelectedOrganisationsChange}
          onPageLoad={handleOrganisationsPageLoad}
          isOrganisationsExpanding={!searchQuery?.length}
          onOrganisationsParentIdChange={handleOrganisationsParentIdChange}
          isMultiple
        />
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button text="Сохранить" color="primary" id="saveButton" size="medium" onClick={handleApply} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IDictionaryKnoOrganisationsModalStoreProps => ({
  knoOrganizationAdminLevelsSelectOptions: getCatalogKnoAdminLevelsSelectOptions(state),
  adminLevelCode: getUserInfoOrganisationsAdminLevelCode(state),
  searchQuery: getUserInfoOrganisationsSearchQuery(state),
  organisationId: getUserInfoOrganisationsParentId(state),
  knoOrganisations: getUserInfoKnoOrganisations(state),
  totalCount: getUserInfoOrganisationsTotalCount(state),
  highlightedOrganisations: getUserInfoHighlightedSelectedOrganisations(state),
});

export const DictionaryKnoOrganisationsModal = connect(mapStateToProps)(DictionaryKnoOrganisationsModalWithoutStore);
